
import { Groupbutton, Wrap, ModalContainer, Geometry, Distance } from './styles';

import Layer from 'services/models/Layer';
import geoConfirm from 'services/utils/confirm';
import { measureArea } from 'services/utils/measures';

import { BufferIcon, TrashIcon, CheckIcon, CloseIcon } from './components/Icons';
import Select from './components/Select';

import ButtonTooltip from '../../ButtonTooltip';

import { CustomModalSaveAsLayer } from 'views/MapView/components/CustomModalSaveAsLayer';


/**
 * Turf é uma coleção de módulos com algoritmos geográficos.
 */
import * as turf from '@turf/turf';
/**
 * useFormik é um hook personalizado do React que retornará
 * todos os estados e auxiliares do Formik diretamente.
 */
import CustomDraggable from 'components/ModalDraggable';
import { useFormik } from 'formik';
/**
 * Importação da biblioteca leaflet a qual utiliza o OpenStreetMap,
 * é uma biblioteca de mapeamento leve e de codigo aberto, utiliza um banco
 * de dados geografico editável gratuito.
 */
import L from 'leaflet';
/**
 * useState é um hook que nos permite rastrear o estado em um
 * componente de função, o estado geralmente se refere a dados ou
 * propriedads que precisam ser rastreador em um aplicativo.
 * 
 * useEffect é um hook que permite executar efeitos colaterais em
 * seus componentes, por exemplo: buscar dados, atualizar diretamente
 * o DOM e cronômetros, ele aceita dois argumentos o segundo é opcional.
 * 
 * useCallback é um hook que retorna uma função de retorno
 * de chamada memorizada
 */
import { useState, useEffect, useCallback } from 'react';
import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import SliderColorPicker from 'views/MapView/components/SliderColorPicker';
/**
 *  useMap é um hook personalizado criado para gerenciar a comunicação
 *  dos componentes com o mapa.
 */
import { useMap } from 'views/MapView/hooks';
import * as Yup from 'yup';

/**
 * Ferramenta Buffer
 * Uma funcionalidade usada em camadas importadas, com ela é possivel 
 * criar um poligo ou uma area em torno das regioes no mapa importadas pela 
 * camada, essa area é determinada pelo usuario atraves da ferramenta, sendo
 * possivel escolher cor e tambem salvar/deletar.
 * 
 * @param {string} mapContext Context
 * @param {boolean} isActive Verificação
 * @param {boolean} setIsActive Seta o estado
 * @param {*} props Resto
 * @returns 
 */
export const Buffer = ({ mapContext, isActive, setIsActive, ...props }) => {
  const [layer, setLayer] = useState(null);
  const [distance, setDistance] = useState(null);
  const [unit, setUnit] = useState({ label: 'km', value: 'kilometers' });
  const [areaPolygon, setAreaPolygon] = useState({ label: 'Considerar', value: 'consider' });
  // const [edge, setEdge] = useState({label: 'Preservar', value: 'preserve'});
  // const [extend, setExtend] = useState({label: 'Ambos os lados', value: 'bothsides'});

  const [showModal, setShowModal] = useState(false);

  const [saveModalOpen, setSaveModalOpen] = useState(false);

  const [applyPreview, setApplyPreview] = useState(false);

  const [applyDistance, setApplyDistance] = useState(null);

  const { layers, map, addLayer } = useMap();

  const formik = useFormik({
    initialValues: {
      layerName: '',
      layerStyle: null,
    },
    validationSchema: Yup.object().shape({
      layerName: Yup.string().required('Campo obrigatório'),
    }),
    onSubmit: (values) => {
      saveAsLayer(values.layerName, values.layerStyle);
    },
  });
  
  //reseta formik
  const { resetForm } = formik;

  //Obtém o tipo do dado da camada que será realizado o buffer
  const getDataType = () => {
    let geometry;
    if(showModal && layer?.dataType){
      if(layer.dataType === "Point"){
        geometry = "Ponto"
      }else if(layer.dataType === "LineString"){
        geometry = "Linha"
      }else{
        geometry = "Polígono"
      }
    }
    return geometry;
  };

  /**
   * Realiza o buffer nas features selecionadas e retorna o geoJson da nova feature
   */
  const getBufferedFeatures = useCallback((distance) => {
    let distanceValue = distance
      ? distance.replace('.', '').replace(',', '.')
      : '';
    distanceValue = distanceValue.replace('-', '').length
      ? parseFloat(distanceValue)
      : 0;
    if (layer && layer.selectedFeatures?.length && isActive && distanceValue) {      
      let arrSelectedFeatures = [];
      layer.selectedFeatures.forEach((sf) => {
        // Foi necessário recriar o objeto para que
        // não sobrescrevesse as propriedades do objeto inicial
        arrSelectedFeatures.push({
          geometry: sf.geometry,
          properties: {},
          type: sf.type,
        });
      });
     
      if(areaPolygon.value==="disregard"){
        //array que armazena as feicoes que sofreram o buffer.
        let arrBufferFeatures = [];
        //array que armazena as feicoes que sofreram o recorte pela feição sem buffer.
        let eraseArea = []; 
        //forEach para percorrer o array de feiçoes e efetuar o buffer.
        arrSelectedFeatures.forEach((e)=>{
          arrBufferFeatures.push( turf.buffer(e, distanceValue, {
            units: unit.value,
          }));
        });
        //forEach para percorrer o array de feiçoes e efetuar o recorte
        arrBufferFeatures.forEach((b,i)=>{eraseArea.push(turf.difference(arrBufferFeatures[i], arrSelectedFeatures[i]))});  
        //retorna array de feições bufferizadas e recortadas pelas áreas das feições selecionada.
        return eraseArea;
      }
      else{
        // variavel que armazena o array de feições selecionadas transformadas em featureCollection.
        let collection = turf.featureCollection(arrSelectedFeatures);
        //retorna uma featureCollection com o buffer aplicado e sem recorte de área.
        return (turf.buffer(collection, distanceValue, {
          units: unit.value,
        }));       
      }
    }
    return null;
  }, [areaPolygon.value, isActive, layer, unit.value]);

  /**
   * Salva a operação de buffer realizada em uma nova camada, que será lista no menu
   * @param {*} layerName Nome que deverá aparecer no menu
   * @param {*} layerStyle Estilo de como a camada deverá ser representada no mapa
   */
  const saveAsLayer = (layerName, layerStyle) => {
    let buffer = getBufferedFeatures(distance);    
    if (buffer) {
      const customRender = L.canvas({ pane: 'mixedPane' });
      let options = {
        pane: 'mixedPane',
        renderer: customRender,
        style: layerStyle,
      };

      const geoJsonLayer = L.geoJSON(buffer, options);
      let layerToAdd = new Layer(
        null,
        'criada',
        null,
        layerName,
        null,
        null,
        null,
        null,
        null
      );      
      layerToAdd.setLeafletLayer(geoJsonLayer);
      layerToAdd.setLayerStyle(layerStyle);      
      let countIdFeature = 1;      
      layerToAdd.leafletLayer.eachLayer((feature) => {                
        feature.feature.properties.ID_GBA = countIdFeature;
        countIdFeature++;        
        feature.feature.properties['Área (m²)'] = measureArea(feature.feature.geometry.coordinates, true);
        feature.feature.properties['Área (ha)'] = measureArea(feature.feature.geometry.coordinates, true) / 10000;
        // eslint-disable-next-line no-useless-concat
        feature.feature.properties['Distância' + ' (' + unit.label + ')'] = distance;
      });
      addLayer(layerToAdd);
      setIsActive(false);
    }
  };

  /**
   * Reseta formik para estado inicial e fecha modal
   */
  const handleCloseSaveModal = useCallback(() => {
    resetForm();
    setSaveModalOpen(false);
  }, [resetForm]);

  /** EFFECTS */

  /**
  * Cria layer temporária para exibição do buffer
  */
  useEffect(() => {
     let buffer = getBufferedFeatures(applyDistance);

     if (applyPreview===true) {
       let geoJsonLayer = L.geoJSON(buffer);
       geoJsonLayer.addTo(map);
       return () => {
       geoJsonLayer.remove();
       };
     }
    },[applyDistance, applyPreview, getBufferedFeatures, map]);
  /**
   * Habilita ferramenta de acordo com a camada selecionada
   */
  useEffect(() => {
    let selectedLayers = layers.filter(
      (l) => l.isSelected && l.visible && l.selectedFeatures.length
    );
    if (
      selectedLayers.length === 1 &&
      (['importada', 'criada'].includes(selectedLayers[0].type) ||
        selectedLayers[0].getService() === 'WFS')
    ) {
      setLayer(selectedLayers[0]);
    } else {
      setLayer(null);
    }
  }, [layers]);

  /**
   * Desabilita ferramenta caso não haja camada selecionada
   */
  useEffect(() => {
    if (!layer && isActive) {
      setIsActive(false);
    }
  }, [layer, isActive, setIsActive]);

  /**
   * Reseta ferramenta ao ser desabilitada
   */
  useEffect(() => {
    if (!isActive) {
      setShowModal(false);
      setDistance(null);
      setUnit({label: 'km', value: 'kilometers'});
      setAreaPolygon({ label: 'Considerar', value: 'consider' });
      setApplyPreview(false);
      handleCloseSaveModal();
    }
    
  }, [isActive, map, handleCloseSaveModal]);

  return (
    <Wrap>
      <ButtonTooltip
        tooltip={Buffer.properties.alias}
        description={Buffer.properties.description}
        onClick={(e) => {
          setIsActive(!isActive);
          setShowModal(true);
        }}
        active={isActive}
        disabled={!layer}
      >
        <BufferIcon style={{ verticalAlign: 'middle' }} />
      </ButtonTooltip>

      {isActive && showModal && (
      <CustomDraggable>
      <ModalContainer style={{ zIndex:1700 }}>
        <div className="modal-int">
          <div className="wrap-title">
            <span>Buffer</span>
            <div
              className="close-modal"
              onClick={() => {
                setIsActive(!isActive);
                setShowModal(false);
              }}
            >
              <CloseIcon width="24" height="24" />
            </div>
          </div>
          <div className="form-group">
                <div className="row-1">
                  <Geometry>
                    <label>Geometria</label>
                    <span className="typeGeometry">
                      {getDataType()}
                    </span>
                  </Geometry> 
                  <Distance>
                    <label>Distância de buffer</label>
                    <div className="distanceBuffer">
                    <MaskedInput
                    type="text"
                    placeholder="00.00000"
                    value={distance}
                    mask={createNumberMask({
                      prefix: '',
                      allowDecimal: true,
                      decimalSymbol: ',',
                      thousandsSeparatorSymbol: '.',
                      decimalLimit: 5,
                      allowNegative: true,
                    })}
                    onChange={(e) => setDistance(e.target.value)}
                    />
                    <hr className="lineLeft"/>
                    <Select
                      width={64}
                      value={unit}
                      options={[
                        { label: 'm', value: 'meters' },
                        { label: 'km', value: 'kilometers' },
                      ]}
                      onChange={(v) => setUnit(v)}
                    />
                    </div>
                  </Distance>
                </div>
                {getDataType()==="Polígono"&&
                  <div className="row-2">
                    {getDataType()==="Polígono"&&
                      <div className="col-1">
                        <label>Área do poligono</label>
                        <Select
                          width={155}
                          value={areaPolygon}
                          options={[
                            {label: 'Considerar', value: 'consider'},
                            {label: 'Desconsiderar', value: 'disregard'}
                          ]}
                          onChange={(v) => setAreaPolygon(v)}
                        />
                      </div>
                    }
                  {/* <div className="col-2">
                    <label>Aresta/Ângulo</label>
                    <Select
                      width={125}
                      value={edge}
                      options={[
                        {label: 'Preservar', value: 'preserve'},
                        {label: 'Arredondar', value: 'round'}
                      ]}
                      onChange={(v) => setEdge(v)}
                    />
                  </div>
                    {/* {getDataType()==="Linha"&&
                      <div className="col-3">
                        <label>Estender para</label>
                        <Select
                          width={155}
                          value={extend}
                          options={[
                            {label: 'Ambos os lados', value: 'bothsides'},
                            {label: 'Direita', value: 'right'},
                            {label: 'Esquerda', value: 'left'}
                          ]}
                          onChange={(v) => setExtend(v)}
                        />
                      </div>
                    } */}
                  </div>
                }
                <Groupbutton>
                  <div className="wrap-buttons">
                    <div className="buffer-actions">
                      <ButtonTooltip
                        tooltip="Remover Buffer"
                        disabled={!distance}
                        activeWithBgColor
                        onClick={() => {
                          geoConfirm({
                            message: 'O buffer será apagado. Deseja continuar? ',
                            callback: (confirmed) => {
                              if (confirmed) {
                                setApplyPreview(false);
                                setDistance(null);
                              }
                            },
                          });
                        }}
                      >       
                        <TrashIcon />
                      </ButtonTooltip>

                      <ButtonTooltip
                        tooltip="Salvar"
                        disabled={!distance}
                        activeWithBgColor
                        onClick={() => setSaveModalOpen(true)}
                      >
                        <CheckIcon />
                      </ButtonTooltip>

                      <ButtonTooltip
                      tooltip="Aplicar Buffer"
                      disabled={!distance}
                      activeWithBgColor
                      onClick={()=>{
                        setApplyDistance(distance);
                        setApplyPreview(true);
                      }}
                      >       
                        <span style={{fontSize: "12px", lineHeight: "16px"}}>
                          Aplicar
                        </span>
                      </ButtonTooltip>
                    </div>
                  </div>
                </Groupbutton>
            </div>
        </div>
        </ModalContainer>
      </CustomDraggable>
      )}

       <CustomModalSaveAsLayer
        title="Salvar Camada"
        saveModalOpen={saveModalOpen}
        handleCloseSaveModal={handleCloseSaveModal}
        formik={formik}
        SliderColorPicker={<SliderColorPicker
          geometryType="Polygon"
          autoSelect={false}
          selectedSymbology={formik.values.layerStyle}
          onSelect={(style) => formik.setFieldValue('layerStyle', style)}
        />}
      />
    </Wrap>
  );
};

Buffer.properties = {
  id: 'CPN-TB-A-BUFFER',
  alias: 'Buffer',
  description: 'Realiza a bufferização de feições. Selecionando uma camada criada ou importada e suas respectivas feições.',
};

export default Buffer;
